import React, {useEffect, useMemo} from 'react';
import {NavLink, useNavigate} from 'react-router-dom';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {AppDispatch, RootState} from '../../redux/store';
import {useOrderConfirmMutation} from '../../redux/userOrdersApi';
import {OrderConfirmDataType} from '../../types/TypesUserOrders';
import {handleApiCall} from '../../assets/utils/utils';
import {setCart} from '../../redux/appSlice';
import {useGetCartQuery} from '../../redux/userApi';
import {usePortmonePayment} from './hooks/usePortmonePayment';
import BuyerInfoSection from './components/BuyerInfoSection';
import PaymentMethodSection from './components/PaymentMethodSection';
import DeliverySection from './components/DeliverySection';
import style from '../CreateInvoice/CreateInvoice.module.css';
import s from './Cart.module.css';

const NewCart: React.FC = () => {
	const dispatch: AppDispatch = useDispatch();
	const {t} = useTranslation();
	const navigate = useNavigate();
	const [orderConfirm] = useOrderConfirmMutation();
	const {submitPayment} = usePortmonePayment();

	const apiData = useSelector((state: RootState) => state.appData.appData);
	const session = useSelector((state: RootState) => state.appData.session);
	const {data: cartData, isLoading, error} = useGetCartQuery();

	// Вычисление максимального бонуса с использованием useMemo
	const maxBonus = useMemo(
		() =>
			Math.min(
				session?.cart?.pay_money || 0,
				Number(session?.cart?.balance_bonus ?? 0),
			),
		[session?.cart?.pay_money, session?.cart?.balance_bonus],
	);

	// Схема валидации с улучшенными проверками
	const validationSchema = useMemo(
		() =>
			Yup.object({
				bonus: Yup.number()
					.min(0, t('Мінімальне значення 0'))
					.max(maxBonus, t('Перевищує максимум'))
					.nullable(),
				approveCheckBox: Yup.boolean().oneOf(
					[true],
					t('Необхідно прийняти умови'),
				),
				payment_method: Yup.string().required(t('Оберіть спосіб оплати')),
				address_id: Yup.number()
					.min(1, t('Оберіть адресу доставки'))
					.required(t('Оберіть адресу доставки')),
				contact_id: Yup.string().required(t('Оберіть контактну особу')),
			}),
		[maxBonus, t],
	);

	const initialValues = {
		order_id: session.cart?.order_id ?? null,
		comment: '',
		address_id: 0,
		contact_id: '',
		bonus: 0,
		pay_money: session.cart?.pay_money ?? null,
		deferred: false,
		payment_method: '',
		approveCheckBox: false,
		service_type: '',
		service_token: '',
		address_name: '',
		building: '',
		city_name: '',
		country_name: '',
		flat: '',
		region_name: '',
		address_ref: '',
		delivery_typeForNP: '',
		delivery_type: 'door',
		country_ref: '',
		region_ref: '',
		city_ref: '',
		recipient_name: '',
		recipient_phone: '',
		newContactMiddlename: '',
		newContactPhone: '',
		newContactSurname: '',
		newContactName: '',
	};

	// Обновление корзины с оптимизированными зависимостями
	useEffect(() => {
		if (cartData?.response) {
			dispatch(
				setCart({
					...cartData.response,
					pay_money: cartData.pay_money,
				}),
			);
		}
	}, [cartData?.response, cartData?.pay_money, dispatch]);

	const handleSubmit = async (values: typeof initialValues) => {
		const data: OrderConfirmDataType = {
			bonus: Number(values.bonus) || 0,
			address_id: values.address_id,
			comment: values.comment,
			contact_id: Number(values.contact_id),
			deferred: values.deferred,
			pay_money: Number(values.pay_money ?? 0),
			order_id: Number(values.order_id ?? 0),
			payment_method: values.payment_method,
		};

		await handleApiCall({
			apiCall: () => orderConfirm(data),
			onSuccess: async (responseData) => {
				if (responseData.approve) {
					if (values.payment_method !== 'card') {
						navigate(`${responseData?.href}`);
					} else {
						// Использование хука для обработки платежа через Portmone
						submitPayment(responseData);
					}
				} else {
					toast.error(responseData.message?.textmsg || t('Помилка оформлення'));
				}
			},
		});
	};

	// Отображение состояния загрузки
	if (isLoading) {
		return (
			<main className="page">
				<div className="page__content container">
					<div className={s.emptyCartContainer}>{t('Завантаження...')}</div>
				</div>
			</main>
		);
	}

	// Отображение ошибки
	if (error) {
		return (
			<main className="page">
				<div className="page__content container">
					<div className={s.emptyCartContainer}>
						{t('Помилка завантаження корзини')}
					</div>
				</div>
			</main>
		);
	}

	return (
		<main className="page">
			<div className="page__content container">
				{session.cart ? (
					<Formik
						validationSchema={validationSchema}
						enableReinitialize
						initialValues={initialValues}
						onSubmit={handleSubmit}
					>
						{({values, errors, touched, handleSubmit, setFieldValue}) => {
							return (
								<Form onSubmit={handleSubmit}>
									<div className={style.topRevertTitle}>
										<NavLink to="/" className={style.backArrow}>
							{t('Назад')} <span />{' '}
										</NavLink>
									</div>

									<div className={s.title}>
										<h1>{t('Оформлення замовлення')}</h1>
									</div>

									<BuyerInfoSection
										cartData={session.cart}
										activeMonth={apiData?.active_month}
										values={{
											deferred: values.deferred,
											bonus: values.bonus,
										}}
										maxBonus={maxBonus}
										setFieldValue={setFieldValue}
										errors={errors}
										touched={touched}
									/>

									<div className={s.methodsWrapper}>
										<PaymentMethodSection />
										<DeliverySection
											supplierId={session.cart?.supplier_id ?? 0}
											values={{
												comment: values.comment,
												approveCheckBox: values.approveCheckBox,
											}}
											setFieldValue={setFieldValue}
										/>
									</div>
								</Form>
							);
						}}
					</Formik>
				) : (
					<div className={s.emptyCartContainer}>
						{t('Ваш кошик порожній :(')}
					</div>
				)}
			</div>
		</main>
	);
};

export default NewCart;
